import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import "./spinner.css";
import { cn } from "./cn";

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: SpinnerSize;
  /** Accessible label announced to assistive tech (the spinner has role="status"). */
  label?: string;
}

/**
 * Indeterminate activity indicator. Token-driven; inherits `currentColor` (defaults to accent)
 * so it matches its context — e.g. inside a button it takes the button's text color. Animation
 * is disabled under `prefers-reduced-motion`; the role="status" + label still convey state.
 */
export function Spinner({ size = "md", label, className, ...rest }: SpinnerProps) {
  const { t } = useTranslation("shared");
  return (
    <span
      role="status"
      aria-label={label ?? t("spinner.loading")}
      className={cn("rc-spinner", `rc-spinner--${size}`, className)}
      {...rest}
    />
  );
}
